import { useEffect, useMemo } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useProfileStore } from "~/store/profileStore";
import { Database } from "~/types/supabase";

import { ClientPicker } from "./ClientPicker";
import { useAssignTotemsToClient, useMoveTotems } from "../hooks/useTotems";
import { getBucket, TotemBucketKey } from "../utils/buckets";

type TotemStatus = Database["public"]["Enums"]["totem_status"];

export interface MoveTotemsSource {
  // Statuses the totems are currently in (the origin bucket's member statuses).
  statuses: TotemStatus[];
  // How many totems are available to move from this source.
  count: number;
  // Set only for the "from a client" flow — scopes the move and stamps provenance.
  clientId?: string;
  clientName?: string;
  // Fallback label when there is no client (e.g. a warehouse bucket).
  label?: string;
}

interface MoveTotemsDialogProps {
  source: MoveTotemsSource | null;
  // Bucket keys offered as destinations. "en_cliente" is allowed for warehouse
  // sources — it shows a client picker and assigns the totems to that client.
  destinations: TotemBucketKey[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MoveTotemsDialog({
  source,
  destinations,
  open,
  onOpenChange,
}: MoveTotemsDialogProps) {
  const { id: userId } = useProfileStore();
  const moveTotems = useMoveTotems();
  const assignToClient = useAssignTotemsToClient();

  const count = source?.count ?? 0;

  // All passed destinations are selectable — warehouse buckets (bulk move to their
  // target status) plus "En Cliente" (assign to a picked client).
  const destBuckets = useMemo(
    () => destinations.map((key) => getBucket(key)),
    [destinations]
  );

  const moveSchema = useMemo(
    () =>
      z
        .object({
          quantity: z.coerce
            .number()
            .int()
            .min(1, "Mínimo 1")
            .max(count || 1, `Máximo ${count}`),
          destination: z.string().min(1, "Selecciona un destino"),
          clientId: z.string().optional(),
        })
        .refine((v) => v.destination !== "en_cliente" || !!v.clientId, {
          message: "Selecciona un cliente",
          path: ["clientId"],
        }),
    [count]
  );
  type MoveFormData = z.infer<typeof moveSchema>;

  const defaultDestination = destBuckets[0]?.key ?? "";

  const form = useForm<MoveFormData>({
    resolver: zodResolver(moveSchema),
    defaultValues: { quantity: 1, destination: defaultDestination, clientId: "" },
  });

  // Reset each time the dialog opens for a new source.
  useEffect(() => {
    if (open) {
      form.reset({ quantity: 1, destination: defaultDestination, clientId: "" });
    }
  }, [open, source?.clientId, source?.label, defaultDestination, form]);

  const destination = form.watch("destination");
  const isClientDest = destination === "en_cliente";
  const isPending = moveTotems.isPending || assignToClient.isPending;

  const onSubmit = async (values: MoveFormData) => {
    if (!source || !userId) return;

    // "En Cliente": assign the totems to the picked client (status -> at_client).
    if (values.destination === "en_cliente") {
      if (!values.clientId) return;
      await assignToClient.mutateAsync({
        fromStatuses: source.statuses,
        quantity: values.quantity,
        clientId: values.clientId,
        performedBy: userId,
        notes: "Asignado manualmente al cliente",
      });
      onOpenChange(false);
      return;
    }

    const bucket = destBuckets.find((b) => b.key === values.destination);
    if (!bucket || !bucket.target) return;
    await moveTotems.mutateAsync({
      fromStatuses: source.statuses,
      toStatus: bucket.target,
      quantity: values.quantity,
      performedBy: userId,
      notes: bucket.moveNote,
      clientId: source.clientId,
    });
    onOpenChange(false);
  };

  const description = source?.clientName
    ? `${source.clientName} — ${count} tótem(s) en el cliente`
    : `${source?.label ?? "Bodega"} — ${count} tótem(s)`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Mover tótems</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 py-2"
          >
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cantidad</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={count}
                      className="h-9"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="destination"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Destino</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Selecciona un destino" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {destBuckets.map((bucket) => (
                        <SelectItem key={bucket.key} value={bucket.key}>
                          {bucket.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isClientDest && (
              <FormField
                control={form.control}
                name="clientId"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Cliente</FormLabel>
                    <FormControl>
                      <ClientPicker
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="h-9"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" className="h-9" disabled={isPending}>
                {isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isClientDest ? "Asignar" : "Mover"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
